import DataTable, { TableOptions, TableVisibility } from '@/components/data-table/DataTable';
import { ProductFilterItem } from '@/components/CustomFilters/ProductFilters/ProductFilters';
import { Brand } from '@/models/Brand';
import { Metadata, Product, ProductImage, ProductIncludes } from '@/models/Product';
import { Supplier } from '@/models/Supplier';
import { formatDate } from '@/support/String';

import { Component, Ref, Vue } from 'vue-property-decorator';
import ProductVariants from './Tabs/ProductVariants/ProductVariants';
import UploadProductImageDialog from '@/views/Products/UploadProductImageDialog/UploadProductImageDialog';
import BulkUpdateDialog from '@/views/Products/BulkUpdateDialog/BulkUpdateDialog';
import UploadProductCsvDialog from '@/views/Products/UploadProductCsvDialog/UploadProductCsvDialog';
import ErrorHandler from '@/support/ErrorHandler';
import { axiosDownload } from '@/support/Client';
import { ProductVariant } from '@/models/ProductVariant';
import { Category } from '@/models/Category';
import { Options } from '@/components/mi-dialog/MiDialog';
import { AxiosError } from 'axios';
import { thumbnailTransformer } from '@/support/ThumbnailTransformer';

@Component<Products>({
  components: {
    ProductVariants,
    UploadProductImageDialog,
    BulkUpdateDialog,
    UploadProductCsvDialog
  }
})
export default class Products extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */
  @Ref() readonly productDatatable!: DataTable;
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isLoading = false;

  protected isDisplayingQuickAddProductsDialog = false;
  protected isUploadingImages = false;
  protected isBulkUpdating = false;
  protected isUploadingCsv = false;
  protected isExportingProducts = false;

  protected selectedProducts: Product[] = [];
  protected selectedProduct: Product | null = null;

  protected isLoadingFilters = false;
  protected isShowingFilters = false;
  protected filtersHeight = 0;
  protected defaultPage = 1;
  protected defaultProductFilter: ProductFilterItem = {
    brands: [],
    suppliers: [],
  };

  protected visibility: TableVisibility = {
    search: true,
    total: false,
    limit: true,
    pagination: true,
    checkboxes: true,
    title: false,
  };
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted(): void {
    this.initialize();
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected async initialize(): Promise<void> {
    //
  }

  protected showFilters(): void {
    if (! this.isShowingFilters) {
      this.isShowingFilters = true;
      const innerFilters = document.getElementsByClassName('inner-filters');
      this.filtersHeight = innerFilters[0].clientHeight;
    } else {
      this.isShowingFilters = false;
      this.filtersHeight = 0;
    }
  }

  protected handleFilterChanged(filter: ProductFilterItem) {
    this.productFilter = { ...filter };
    // Do not reset the page on reload
    if (! this.isLoadingFilters) {
      this.page = 1;
      localStorage.setItem(`${this.$route.name}_page`, this.page.toString());
    }
  }

  protected openExport(response: string) {

  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async exportProducts(): Promise<void> {
    this.isExportingProducts = true;
    let payload: exportPayload = {};

    if (this.selectedProducts.length) {
      payload.products = this.selectedProducts.map((product: Product) => product.id)
    };

    try {
      await axiosDownload({
        url: new Product().exportProducts(),
        accept: 'text/csv; charset=utf-8'
      }, payload);
    } catch (error) {
      ErrorHandler.network(error);
    }
    this.isExportingProducts = false;
  }
  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */

  protected get productFilter() {
    const filters = localStorage.getItem(`${this.$route.name}_productFilters`);

    if (filters) {
      const parsedobj = JSON.parse(filters);
      this.defaultProductFilter = {...parsedobj, ...this.defaultProductFilter};
    }

    return this.defaultProductFilter;
  }

  protected set productFilter(filter: ProductFilterItem) {
    this.defaultProductFilter = filter;
  }

  protected get limit() {
    const result = localStorage.getItem(`${this.$route.name}_limit`);
    return result ? Number(result) : 25;
  }

  protected get page() {
    const result = localStorage.getItem(`${this.$route.name}_page`);
    return result ? Number(result) : this.defaultPage;
  }

  protected set page(page: number) {
    this.defaultPage = page;
  }

  protected get tableOptions(): TableOptions {
    return {
      model: new Product()
        .filter(this.productFilter)
        .include([
          ProductIncludes.BRAND,
          ProductIncludes.IMAGES,
          ProductIncludes.CATEGORIES,
          ProductIncludes.SUPPLIER,
          ProductIncludes.METADATA
        ]),
      clickableRows: true,
      expandable: true,
      sort: {
        key: 'created_at',
        order: 'DESC'
      },
      headers: [
        {
          text: '',
          value: 'image_url',
          width: '1%',
          transform: (image: string) => image ? thumbnailTransformer(image) : '',
        },
        {
          text: 'Name',
          value: 'name',
          action: '/producten/{id}',
          sortable: {
            key: 'name',
            order: 'DESC'
          },
        },
        {
          text: 'Product ID',
          value: 'product_id',
        },
        {
          text: 'Supplier',
          value: 'supplier',
          transform: (supplier: Supplier) => {
            return supplier ? supplier.name : '';
          }
        },
        {
          text: 'Brand',
          value: 'brand',
          transform: (brand: Brand) => {
            return brand ? brand.name : '';
          }
        },
        {
          text: 'Category',
          value: 'categories',
          transform: (categories: Category[]) => {
            return (categories && categories.length) ? categories.map(category => category.name).join(', ') : '-';
          }
        },
        {
          text: 'Variants',
          value: 'metadata',
          transform: (metadata: Metadata) => {
            return `${metadata && metadata.total_variants ?metadata.total_variants : 0 }`;
          }
        },
        {
          text: 'Stock oh',
          value: 'metadata',
          transform: (metadata: Metadata) => {
            return `${metadata && metadata.stock_on_hand ? metadata.stock_on_hand : 0}`;
          }
        },
        {
          text: 'Stock av',
          value: 'meta',
          transform: (meta: Metadata) => {
            return `${meta && meta.stock_available ? meta.stock_available : 0}`;
          }
        },
        {
          text: 'Stock po',
          value: 'meta',
          transform: (meta: Metadata) => {
            return `${meta && meta.stock_purchase_order ? meta.stock_purchase_order : 0}`;
          }
        },
        {
          text: 'Created',
          value: 'created_at',
          transform: (created_at: string) => {
            return `<span class="d-block" style="width: 100px;">${formatDate(created_at)}</span>`;
          },
        },
        {
          text: 'Last received',
          value: 'last_received_at',
          transform: (last_received_at: string) => {
            return `<span class="d-block" style="width: 100px;">${formatDate(last_received_at)}</span>`;
          },
        },
        {
          text: 'Attr',
          value: 'metadata',
          transform: (metadata: Metadata) => {
            return metadata && metadata.has_missing_attributes ? '<i aria-hidden="true" class="v-icon material-icons theme--light red--text">close</i>' : '<i aria-hidden="true" class="v-icon material-icons theme--light green--text">check</i>';
          },
          width: '100px',
        },
      ],
      actions: [
        {
          type: 'add',
          label: 'add',
          icon: 'collections',
          action: (vm: any, product: Product) => {
            this.selectedProduct = product;
            this.isUploadingImages = true;
          },
          tooltip: 'Upload foto\'s',
        },
        {
          type: 'view',
          label: 'view',
          icon: 'remove_red_eye',
          action: '/producten/{id}',
          tooltip: 'Bekijk product',
        },
        {
          type: "delete",
          label: "delete",
          icon: "delete",
          tooltip: "Verwijder product",
          action: (datatable: DataTable, product: Product) => {
            this.selectedProduct = product;
            this.$store.dispatch("openDialog", this.deleteDialogOptions);
          },
          visible: (datatable: DataTable, product: Product) => ! product.metadata || ! product.metadata.total_variants,
        }
      ],
      filters: [],
    }
  }

  public refreshProductDatatable() {
    this.productDatatable ? this.productDatatable.refresh() : null;
  }

  protected get hasSelectedProducts() {
    return !!this.selectedProducts.length;
  }

  protected get deleteDialogOptions(): Options {
    return {
      title: this.$t("dialogOptions.confirmation").toString(),
      text: "Weet je zeker dat je dit product wilt verwijderen?",
      type: "warning",
      buttons: {
        confirm: {
          text: this.$t("dialogOptions.button.delete").toString(),
          action: async () => {
            if (!this.selectedProduct) { return; }

            await new Product({ id: this.selectedProduct.id })
              .delete()
              .then(() => this.refreshProductDatatable())
              .catch((error: AxiosError) => {
                ErrorHandler.network(error);
              });

            this.selectedProduct = null;
          }
        },
        cancel: {
          text: this.$t("dialogOptions.button.cancel").toString(),
          action: () => {
            this.selectedProduct = null;
          }
        }
      }
    };
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */
  //#endregion
}

interface exportPayload  {
  products?: string[];
}
